/**
 * Display-only aggregation for catalogue **preview** mode.
 * Does not alter buildTimePhasedUnitsSeries or backend analytics.
 */

import type { TimePhasedUnitsPoint } from '@/utils/buildTimePhasedUnitsSeries';

/**
 * Bucket by calendar month; each month's **unitsPerDay** is the average daily load across
 * days present in that month (same magnitude as focus daily bars). Preview only.
 */
export function aggregateAverageUnitsPerDayByCalendarMonth(series: TimePhasedUnitsPoint[]): TimePhasedUnitsPoint[] {
  const byMonth = new Map<string, { sum: number; days: number }>();
  for (const p of series) {
    if (!p.date || p.date.length < 7) continue;
    const m = p.date.slice(0, 7);
    const prev = byMonth.get(m) ?? { sum: 0, days: 0 };
    prev.sum += p.unitsPerDay;
    prev.days += 1;
    byMonth.set(m, prev);
  }
  const keys = [...byMonth.keys()].sort();
  let cumulativeHint = 0;
  return keys.map((ym) => {
    const { sum, days } = byMonth.get(ym)!;
    const avg = days > 0 ? sum / days : 0;
    cumulativeHint += sum;
    return { date: `${ym}-01`, unitsPerDay: avg, cumulativeUnits: cumulativeHint };
  });
}

/** One point per month: last day in series for that month (preserves cumulative at month end). */
export function sampleCumulativeByCalendarMonth(series: TimePhasedUnitsPoint[]): TimePhasedUnitsPoint[] {
  const byMonth = new Map<string, TimePhasedUnitsPoint>();
  for (const p of series) {
    if (!p.date || p.date.length < 7) continue;
    const m = p.date.slice(0, 7);
    const prev = byMonth.get(m);
    if (!prev || p.date > prev.date) byMonth.set(m, p);
  }
  return [...byMonth.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([, v]) => v);
}

export function downsampleAlignedSeries(
  labels: string[],
  seriesData: Array<{ name: string; values: number[] }>,
  maxPoints: number
): { xAxisData: string[]; seriesVals: typeof seriesData } {
  const n = labels.length;
  if (n <= maxPoints || n === 0) {
    return { xAxisData: labels, seriesVals: seriesData };
  }
  const step = (n - 1) / (maxPoints - 1);
  const indices = Array.from({ length: maxPoints }, (_, i) => Math.min(n - 1, Math.round(i * step)));
  const uniq = [...new Set(indices)].sort((a, b) => a - b);
  return {
    xAxisData: uniq.map((i) => labels[i]!),
    seriesVals: seriesData.map((s) => ({
      name: s.name,
      values: uniq.map((i) => s.values[i] ?? 0),
    })),
  };
}
